import FullWidthLayout from "@/layouts/FullWidthLayout";
import OutboundFreightTable from "kinnect-front/Warehouse/OutboundFreight/OutboundFreightTable";
import { Broadcastable, FetchesData, Pageable } from "kinnect-mixins";
import { KDatePicker, KTextField } from "kinnect-general";
import { OutboundFreight } from "kinnect-models";
import {
	VCard,
	VCol,
	VRow,
	VSheet
} from "vuetify/lib";
import { debounce, removeByObjectValue } from "kinnect-helpers";
import { formatDate } from "kinnect-formatters";
import { isBefore, parseISO, startOfDay } from "date-fns";

export default {
	name: "WarehouseOutboundFreightListPage",

	metaInfo: { title: "Warehouse - Outbound Freight" },

	mixins: [
		Broadcastable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		searchOutboundFreight: [],
		outboundFreightList: [],
		search: "",
		today: ( new Date() ).setHours( 0, 0, 0, 0 ),
		outboundFreightIncludes: [
			"address",
			"carrier",
			"customer",
			"sales-orders",
			"salesman.user",
			"statuses",
			"statuses.user-set-by",
			"user-assigned"
		],
		actions: {
			add: {
				icon: "add_circle",
				text: "Add",
				to: { name: "outboundFreightAdd" },
				tooltip: "Add outbound freight"
			},
			request: {
				icon: "add_circle",
				text: "Request Outbound Freight",
				to: { name: "outboundFreightRequest" },
				tooltip: "Request a new outbound freight"
			},
			requests: {
				badge: null,
				icon: "room_service",
				text: "Requests",
				to: { name: "outboundFreightRequests" },
				tooltip: "View outbound freight requests"
			}
		}
	} ),

	computed: {
		date: {
			get() {
				return this.$route.query.date;
			},

			set( date ) {
				this.$router.push( { query: { date } } );
			}
		},

		selectedDate() {
			return this.date || formatDate( this.today, "yyyy-MM-dd" );
		},

		displayDate() {
			return formatDate( this.selectedDate );
		},

		selectedDateOutboundFreight() {
			const selectedDateIso = parseISO( this.selectedDate );

			return this.outboundFreightList.filter( outboundFreight => {
				const existedOnDate = !isBefore(
					selectedDateIso,
					startOfDay( parseISO( outboundFreight.assignedAt ) )
				);
				const notYetArrived = !outboundFreight.estimatedArrivalDate
					|| !isBefore(
						parseISO( outboundFreight.estimatedArrivalDate ),
						selectedDateIso
					);

				return existedOnDate && notYetArrived;
			} );
		},

		displayedOutboundFreight() {
			if ( this.search ) {
				return this.searchOutboundFreight;
			}

			return this.selectedDateOutboundFreight;
		},

		assignedOutboundFreight() {
			return this.displayedOutboundFreight.filter( outboundFreight =>
				outboundFreight.$.status === "ASSIGNED"
			);
		},

		inProgressOutboundFreight() {
			return this.displayedOutboundFreight.filter( outboundFreight =>
				outboundFreight.$.status !== "ASSIGNED"
				&& outboundFreight.$.status !== "COMPLETED"
			);
		},

		completedOutboundFreight() {
			return this.displayedOutboundFreight.filter( outboundFreight =>
				outboundFreight.$.status === "COMPLETED"
			);
		},

		toolbarActions() {
			if ( this.$can( "add_outbound_freight" ) ) {
				return [
					this.actions.add,
					this.actions.requests
				];
			}

			return this.actions.request;
		},

		broadcasts() {
			const broadcasts = [ {
				Model: OutboundFreight,
				channel: "outbound-freight",
				propKey: "outboundFreightList",
				eventNames: {
					c: "outbound-freight.created",
					u: "outbound-freight.updated",
					d: "outbound-freight.deleted"
				}
			} ];

			if ( this.$can( "add_outbound_freight" ) ) {
				broadcasts.push( {
					channel: "outbound-freight",
					propKey: "badge",
					propPath: "actions.requests",
					eventNames: {
						r: "outbound-freight.requested.count"
					}
				} );
			}

			return broadcasts;
		}
	},

	watch: {
		selectedDate() {
			this.fetchItems();
		},

		search() {
			this.fetchSearchResults();
		}
	},

	methods: {
		onOutboundFreightRemoved( outboundFreight ) {
			this.outboundFreightList = removeByObjectValue(
				this.outboundFreightList,
				"id",
				outboundFreight.id
			);
		},

		fetchSearchResults: debounce( async function() {
			if ( this.search ) {
				const queryWhenCallStarted = this.search;

				const outboundFreight = await OutboundFreight
				.custom( "warehouse/outbound-freight/search" )
				.params( { query: queryWhenCallStarted } )
				.include( this.outboundFreightIncludes )
				.get();

				if ( this.search === queryWhenCallStarted ) {
					this.searchOutboundFreight = outboundFreight;
				}
			} else {
				this.searchOutboundFreight = [];
			}
		}, 500 ),

		async fetchNumRequests() {
			if ( this.$can( "add_outbound_freight" ) ) {
				this.actions.requests.badge = await this.$http
				.get( "warehouse/outbound-freight/count?filter[approved]=false" )
				.then( resp => resp.data );
			}
		},

		async fetchItems() {
			const dateWhenCallStarted = this.selectedDate;
			const outboundFreightList = await OutboundFreight
			.where( "for_date", dateWhenCallStarted )
			.where( "approved", true )
			.include( this.outboundFreightIncludes )
			.get();

			if ( this.selectedDate === dateWhenCallStarted ) {
				this.outboundFreightList = outboundFreightList;
			}
		},

		async fetch() {
			await this.$http.all( [
				this.fetchItems(),
				this.fetchNumRequests()
			] );
		}
	},

	render() {
		return <FullWidthLayout>
			<VCard class="mt-4">
				<VSheet class="px-3" color="primary" tile>
					<VRow>
						<VCol cols={12} sm={4}>
							<KDatePicker
								showFieldArrows
								textFieldProps={{
									dark: true,
									flat: true,
									hideDetails: true,
									label: "Date*",
									prependIcon: null,
									prependInnerIcon: "event",
									soloInverted: true
								}}
								vModel={this.date}
							></KDatePicker>
						</VCol>

						<VCol cols={12} sm={8}>
							<KTextField
								clearable
								dark
								flat
								hideDetails
								label="Search"
								loading={this.$wait.is( "get outbound freight list" )}
								prependInnerIcon="search"
								soloInverted
								vModel={this.search}
							></KTextField>
						</VCol>
					</VRow>
				</VSheet>

				<OutboundFreightTable
					dense
					items={this.assignedOutboundFreight}
					loading={this.$wait.is( "get outbound freight list" )}
					onOutboundFreightRemoved={this.onOutboundFreightRemoved}
				>
					<template slot="title">
						{this.search
							? <span>Assigned outbound freight for the search "{this.search}"</span>
							: <span>Assigned outbound freight for {this.displayDate}</span>
						}
					</template>
				</OutboundFreightTable>

				<OutboundFreightTable
					dense
					hiddenCols="userAssigned.name"
					items={this.inProgressOutboundFreight}
					loading={this.$wait.is( "get outbound freight list" )}
					onOutboundFreightRemoved={this.onOutboundFreightRemoved}
				>
					<template slot="title">
						{this.search
							? <span>In-progress outbound freight for the search "{this.search}"</span>
							: <span>In-progress outbound freight for {this.displayDate}</span>
						}
					</template>
				</OutboundFreightTable>

				<OutboundFreightTable
					dense
					hiddenCols="userAssigned.name"
					items={this.completedOutboundFreight}
					loading={this.$wait.is( "get outbound freight list" )}
					onOutboundFreightRemoved={this.onOutboundFreightRemoved}
				>
					<template slot="title">
						{this.search
							? <span>Completed outbound freight for the search "{this.search}"</span>
							: <span>Completed outbound freight for {this.displayDate}</span>
						}
					</template>
				</OutboundFreightTable>
			</VCard>
		</FullWidthLayout>;
	}
};
